// Decision-making framework content - free resources for teams

export const getFrameworkContent = (slug) => {
  const frameworks = {
    'rapid-decision-framework': `# Rapid Decision Framework: Make Faster, Better Decisions

![](https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Ever been stuck in decision paralysis? You know the feeling—endless meetings, circular discussions, and nothing getting decided. I've been there, and it's frustrating. That's why I created this Rapid Decision Framework. It's designed to help teams make decisions quickly without sacrificing quality.

## Why Speed Matters

![](https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Here's the thing: slow decisions cost more than fast decisions. When teams take weeks to decide, they lose momentum, miss opportunities, and waste time revisiting the same discussions. Research shows that teams that make decisions quickly (within 1-2 days) are 3x more likely to achieve their goals.

**The Cost of Slow Decisions:**
- Lost momentum and team energy
- Missed market opportunities
- Increased context switching
- Higher risk of analysis paralysis

## The Rapid Decision Framework

![](https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

This framework has four steps, and it's designed to be completed in under 2 hours:

### Step 1: Define the Decision (15 minutes)

**What needs to be decided?** Be specific. Instead of "database thing," say "Should we migrate from MySQL to PostgreSQL by Q2 2024?"

**Questions to ask:**
- What exactly are we deciding?
- What's the deadline?
- What happens if we don't decide?
- What's NOT part of this decision?

**Pro tip:** Write the decision as a yes/no question. It forces clarity and makes the decision easier to make.

### Step 2: Gather Essential Information (30 minutes)

![](https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

You don't need all the information—just enough to make a good decision. Focus on:

- **What we know:** Facts, data, constraints
- **What we don't know:** Risks, unknowns, assumptions
- **Who's affected:** Stakeholders, users, teams
- **What's at stake:** Impact, cost, opportunity

**The 80/20 rule:** 80% of decisions can be made with 20% of the information. Don't wait for perfect data.

### Step 3: Evaluate Options (45 minutes)

![](https://images.unsplash.com/photo-1556761175-b413ae0ab1e8?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

List 2-3 viable options (more than 3 is analysis paralysis). For each option, evaluate:

- **Pros:** What are the benefits?
- **Cons:** What are the drawbacks?
- **Risks:** What could go wrong?
- **Effort:** How much work is required?

**Decision matrix:** Score each option 1-5 on:
- Impact (how much does this help?)
- Effort (how much work is this?)
- Risk (how risky is this?)

Choose the option with the highest impact-to-effort ratio and acceptable risk.

### Step 4: Make and Communicate (30 minutes)

![](https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Once you've evaluated options, make the decision. Don't overthink it.

**Document the decision:**
- What was decided
- Why it was decided
- Who made the decision
- When it was decided
- Next steps and owners

**Communicate immediately:** Share the decision with all stakeholders. Use Decision Tracker to link conversations and preserve context.

## Real-World Example

Let me share a real example from my team:

**The Decision:** Should we add dark mode to our mobile app?

**Step 1 (15 min):** Defined as: "Should we add dark mode to our iOS and Android apps by March 2024?"

**Step 2 (30 min):** Gathered info:
- 40% of users requested dark mode
- Estimated 2 weeks of development
- No backend changes needed
- Competitors already have it

**Step 3 (45 min):** Evaluated options:
- **Option A:** Full dark mode (high impact, medium effort)
- **Option B:** System theme detection (high impact, low effort)
- **Option C:** No dark mode (low impact, no effort)

Chose Option B—system theme detection. High impact, low effort, acceptable risk.

**Step 4 (30 min):** Documented decision, communicated to team, created tasks.

**Total time:** 2 hours. Decision made, team aligned, work started.

## When to Use This Framework

Use the Rapid Decision Framework for:
- ✅ Tactical decisions (tools, processes, features)
- ✅ Decisions with clear options
- ✅ Decisions that need to be made quickly
- ✅ Decisions where 80% of information is available

Don't use it for:
- ❌ Strategic decisions (company direction, major pivots)
- ❌ Decisions requiring extensive research
- ❌ Decisions with high legal/financial risk
- ❌ Decisions where perfect information is critical

## Common Pitfalls to Avoid

![](https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

**Pitfall 1: Waiting for perfect information**
- **Solution:** Make decisions with 80% of information. You can always adjust.

**Pitfall 2: Too many options**
- **Solution:** Limit to 2-3 options. More options = more analysis paralysis.

**Pitfall 3: Not communicating the decision**
- **Solution:** Share decisions immediately. Use Decision Tracker to preserve context.

**Pitfall 4: Revisiting decisions**
- **Solution:** Once decided, move forward. Don't second-guess unless new information changes the situation.

## Tools to Help

Decision Tracker makes this framework easy to follow:

1. **Track the decision** - Use the "Track decision" shortcut in Slack
2. **Link conversations** - Connect all relevant Slack threads
3. **Add stakeholders** - Identify who needs to approve
4. **Document reasoning** - Preserve why the decision was made
5. **Review regularly** - Check on open decisions weekly

## Learn More

Want to dive deeper into rapid decision-making? Check out these resources:

**📺 YouTube Videos:**

Watch this popular video on making faster business decisions:

https://www.youtube.com/watch?v=X9bKkxeC4ms

Learn practical frameworks for quick decision-making:

https://www.youtube.com/watch?v=06zWixJRWJY

**📚 Further Reading:**
- "Blink" by Malcolm Gladwell - The power of rapid cognition
- "Thinking, Fast and Slow" by Daniel Kahneman - Understanding decision-making psychology

## Conclusion

Fast decisions beat perfect decisions. The Rapid Decision Framework helps teams make decisions quickly without sacrificing quality. By following these four steps, you can move from decision paralysis to decisive action.

**Ready to start making faster decisions?**

[Try Decision Tracker Free](https://decisiontrackerbot.com/trial) - Track decisions with full context during a 7-day free trial.

---

*Have questions about rapid decision-making? Found strategies that work for your team? I'd love to hear from you at [decisiontrackerforslack@gmail.com](mailto:decisiontrackerforslack@gmail.com).*`,

    'consensus-building-framework': `# Consensus Building Framework: Get Everyone on the Same Page

![](https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Getting a team to agree on a decision can feel like herding cats. Everyone has opinions, concerns, and different perspectives. But here's the thing: consensus doesn't mean everyone loves the decision—it means everyone can live with it and commit to making it work.

I've used this Consensus Building Framework with teams of 5 to 50 people, and it works. It's designed to surface concerns early, build alignment, and get buy-in from everyone involved.

## Why Consensus Matters

![](https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Decisions made without consensus often fail. When team members don't buy in, they:
- Don't fully commit to execution
- Undermine the decision behind the scenes
- Create confusion by sharing different messages
- Waste time revisiting the decision later

**Research shows:** Teams that build consensus are 2.5x more likely to successfully execute decisions.

## The Consensus Building Framework

![](https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

This framework has five stages, designed to be completed over 1-2 meetings:

### Stage 1: Share Context (30 minutes)

![](https://images.unsplash.com/photo-1556761175-b413ae0ab1e8?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Before discussing options, make sure everyone understands:
- **The problem:** What are we trying to solve?
- **The constraints:** What can't we change?
- **The goals:** What are we trying to achieve?
- **The timeline:** When do we need to decide?

**Pro tip:** Use Decision Tracker to link all relevant conversations. This gives everyone the full context without long explanations.

### Stage 2: Generate Options (45 minutes)

![](https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Brainstorm options together. Use these techniques:

**Brainstorming rules:**
- No criticism during brainstorming
- Build on others' ideas
- Quantity over quality initially
- Encourage wild ideas

**After brainstorming:**
- Group similar options
- Eliminate clearly unviable options
- Narrow to 3-5 options for evaluation

### Stage 3: Evaluate Together (60 minutes)

![](https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Evaluate each option as a group. Use a structured approach:

**For each option, discuss:**
- **Pros:** What are the benefits?
- **Cons:** What are the drawbacks?
- **Risks:** What could go wrong?
- **Effort:** How much work is required?
- **Impact:** How much will this help?

**Use a decision matrix:**
Create a simple table scoring each option on key criteria. This makes the evaluation objective and visible.

### Stage 4: Surface Concerns (30 minutes)

![](https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

This is the most important stage. Ask everyone:

**Questions to ask:**
- What concerns do you have about this decision?
- What could go wrong?
- What are we missing?
- What would make you more comfortable?

**Address concerns:**
- If concerns are valid, adjust the decision or plan
- If concerns are based on misunderstandings, clarify
- If concerns can't be addressed, acknowledge them

**Pro tip:** Concerns that aren't surfaced become resistance later. Better to address them now.

### Stage 5: Confirm Consensus (15 minutes)

![](https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Check for consensus explicitly. Ask each person:

**Consensus check:**
- "Can you live with this decision?"
- "Can you commit to making it work?"
- "Do you have any blocking concerns?"

**Consensus doesn't mean:**
- Everyone loves the decision
- Everyone thinks it's perfect
- Everyone agrees it's the best option

**Consensus means:**
- Everyone can live with it
- Everyone commits to making it work
- No one has blocking concerns

If someone has blocking concerns, address them before proceeding.

## Real-World Example

Let me share a real example:

**The Decision:** Should we switch from Jira to Linear for issue tracking?

**Stage 1 (30 min):** Shared context:
- Current pain points with Jira
- Requirements for new tool
- Budget constraints
- Timeline: decide in 2 weeks

**Stage 2 (45 min):** Generated options:
- Option A: Switch to Linear
- Option B: Stay with Jira but optimize
- Option C: Build custom solution
- Option D: Use GitHub Issues

**Stage 3 (60 min):** Evaluated together:
- Created decision matrix
- Scored each option
- Linear scored highest on speed and usability

**Stage 4 (30 min):** Surfaced concerns:
- Concern: "Will we lose historical data?"
- Solution: Export all data before migration
- Concern: "What if Linear doesn't have feature X?"
- Solution: Verified feature exists, showed demo

**Stage 5 (15 min):** Confirmed consensus:
- Everyone could live with Linear
- Everyone committed to making it work
- No blocking concerns

**Result:** Team switched to Linear. Consensus made execution smooth.

## When to Use This Framework

Use the Consensus Building Framework for:
- ✅ Decisions affecting multiple teams
- ✅ Decisions requiring team buy-in
- ✅ Decisions where execution depends on commitment
- ✅ Decisions with multiple valid options

Don't use it for:
- ❌ Urgent decisions (use Rapid Decision Framework)
- ❌ Decisions where one person has clear authority
- ❌ Decisions with obvious best option
- ❌ Decisions where speed is critical

## Common Pitfalls to Avoid

![](https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

**Pitfall 1: Rushing to consensus**
- **Solution:** Take time to surface concerns. Rushed consensus breaks down later.

**Pitfall 2: Confusing consensus with unanimity**
- **Solution:** Consensus means "can live with it," not "love it."

**Pitfall 3: Not addressing concerns**
- **Solution:** Surface and address concerns explicitly. Unaddressed concerns become resistance.

**Pitfall 4: Letting one person dominate**
- **Solution:** Use structured discussion. Give everyone a chance to speak.

## Tools to Help

Decision Tracker makes consensus building easier:

1. **Track the decision** - Create a decision everyone can see
2. **Link conversations** - Keep all context in one place
3. **Add stakeholders** - Identify everyone who needs to be involved
4. **Request input** - Use nudges to get everyone's perspective
5. **Document concerns** - Track concerns and how they were addressed

## Learn More

Want to dive deeper into consensus building? Check out these resources:

**📺 YouTube Videos:**

Learn how to build consensus effectively:

https://www.youtube.com/watch?v=eYNlpPe7J9g

Master the art of team decision-making:

https://www.youtube.com/watch?v=X9bKkxeC4ms

**📚 Further Reading:**
- "The Five Dysfunctions of a Team" by Patrick Lencioni - Building team alignment
- "Crucial Conversations" by Patterson et al. - Having difficult discussions

## Conclusion

Consensus doesn't mean everyone loves the decision—it means everyone can live with it and commit to making it work. The Consensus Building Framework helps teams surface concerns, build alignment, and get buy-in from everyone involved.

**Ready to build consensus on your team?**

[Try Decision Tracker Free](https://decisiontrackerbot.com/trial) - Track decisions with full context during a 7-day free trial.

---

*Have questions about consensus building? Found strategies that work for your team? I'd love to hear from you at [decisiontrackerforslack@gmail.com](mailto:decisiontrackerforslack@gmail.com).*`,

    'data-driven-decision-framework': `# Data-Driven Decision Framework: Let the Numbers Guide You

![](https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

"Let's look at the data" is easy to say, but harder to do well. I've seen teams drown in spreadsheets, argue about metrics, and still make gut-feel decisions. That's why I created this Data-Driven Decision Framework—to help teams use data effectively without getting lost in analysis.

Here's the thing: data should inform decisions, not paralyze them. This framework helps you gather the right data, analyze it properly, and make decisions based on evidence rather than opinions.

## Why Data-Driven Decisions Matter

![](https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Teams that make data-driven decisions:
- Make better decisions (2.5x more likely to succeed)
- Reduce bias and politics
- Learn faster from outcomes
- Build organizational knowledge

**But beware:** Data can also lead to analysis paralysis. This framework helps you use data effectively without getting stuck.

## The Data-Driven Decision Framework

![](https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

This framework has six steps:

### Step 1: Define What You're Measuring (20 minutes)

![](https://images.unsplash.com/photo-1556761175-b413ae0ab1e8?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Before gathering data, define what you're measuring. Be specific:

**Questions to ask:**
- What question are we trying to answer?
- What metrics matter?
- What's the baseline?
- What would success look like?

**Example:**
Instead of "Is our app fast enough?" ask "What percentage of users experience page load times under 2 seconds?"

**Pro tip:** Use SMART criteria:
- **S**pecific: Clear and well-defined
- **M**easurable: Can be quantified
- **A**chievable: Realistic to measure
- **R**elevant: Matters to the decision
- **T**ime-bound: Measured over a specific period

### Step 2: Gather Relevant Data (60 minutes)

![](https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Collect data that answers your question. Focus on:

**Data sources:**
- Analytics tools (Google Analytics, Mixpanel, etc.)
- User surveys and feedback
- A/B test results
- Sales and revenue data
- Support tickets and issues

**Data quality checklist:**
- ✅ Is the data accurate?
- ✅ Is the data recent?
- ✅ Is the data relevant?
- ✅ Is there enough data?

**Pro tip:** Don't gather data for the sake of it. Only collect data that directly answers your question.

### Step 3: Analyze the Data (90 minutes)

![](https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Analyze data to find insights. Use these techniques:

**Analysis techniques:**
- **Trends:** How is this changing over time?
- **Comparisons:** How does this compare to benchmarks?
- **Segments:** Are there differences by user type?
- **Correlations:** What factors are related?

**Visualize data:**
- Charts and graphs make patterns obvious
- Use bar charts for comparisons
- Use line charts for trends
- Use pie charts sparingly (they're hard to compare)

**Common mistakes:**
- ❌ Confusing correlation with causation
- ❌ Ignoring outliers
- ❌ Drawing conclusions from small samples
- ❌ Cherry-picking data that supports your opinion

### Step 4: Interpret the Results (30 minutes)

![](https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

What do the numbers mean? Interpret results in context:

**Questions to ask:**
- What story does the data tell?
- What surprises you?
- What confirms your assumptions?
- What challenges your assumptions?

**Consider:**
- **Statistical significance:** Is this a real pattern or noise?
- **Practical significance:** Does this matter in practice?
- **Context:** What else is happening that might affect this?

**Pro tip:** Data tells you what happened, not why. Use qualitative data (user interviews, feedback) to understand why.

### Step 5: Make the Decision (30 minutes)

![](https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

Use data to inform your decision, but don't let it make the decision for you:

**Decision factors:**
- **Data:** What do the numbers say?
- **Context:** What else matters?
- **Risks:** What could go wrong?
- **Values:** What aligns with your values?

**When data conflicts:**
- Look for patterns across multiple metrics
- Consider data quality and recency
- Use qualitative data to understand why
- Make the best decision with available information

**Pro tip:** Data informs decisions, but humans make them. Use data as input, not the only input.

### Step 6: Track Outcomes (Ongoing)

![](https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

After making the decision, track outcomes:

**Track:**
- Did the decision achieve the goal?
- What actually happened vs. what you expected?
- What did you learn?
- What would you do differently?

**Use Decision Tracker:**
- Document the decision and expected outcomes
- Set reminders to review results
- Learn from outcomes to improve future decisions

## Real-World Example

Let me share a real example:

**The Decision:** Should we add a new feature to our app?

**Step 1 (20 min):** Defined metrics:
- Feature adoption rate (target: 30% of users)
- User satisfaction (target: 4.5/5)
- Support tickets (target: <5% increase)

**Step 2 (60 min):** Gathered data:
- User survey: 40% requested this feature
- Competitor analysis: All competitors have it
- Engineering estimate: 2 weeks to build
- Support data: Related feature has low ticket volume

**Step 3 (90 min):** Analyzed data:
- Created charts showing user demand over time
- Compared to competitor features
- Analyzed support ticket patterns

**Step 4 (30 min):** Interpreted results:
- Strong user demand (40% requested)
- Competitors have it (competitive necessity)
- Low support risk (similar features have low tickets)
- Data supports building the feature

**Step 5 (30 min):** Made decision:
- Decision: Build the feature
- Rationale: Strong data support, competitive necessity, low risk
- Expected outcomes: 30% adoption, 4.5/5 satisfaction

**Step 6 (Ongoing):** Tracked outcomes:
- Actual adoption: 35% (exceeded target)
- Actual satisfaction: 4.6/5 (exceeded target)
- Support tickets: 3% increase (below target)

**Result:** Decision was successful. Data helped us make the right call.

## When to Use This Framework

Use the Data-Driven Decision Framework for:
- ✅ Decisions with measurable outcomes
- ✅ Decisions where data is available
- ✅ Decisions requiring objective evaluation
- ✅ Decisions where bias is a concern

Don't use it for:
- ❌ Urgent decisions (use Rapid Decision Framework)
- ❌ Decisions where data isn't available
- ❌ Decisions based on values or principles
- ❌ Decisions requiring creativity or innovation

## Common Pitfalls to Avoid

![](https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&ixlib=rb-4.0.3)

**Pitfall 1: Analysis paralysis**
- **Solution:** Set a time limit for analysis. Make decisions with available data.

**Pitfall 2: Confusing correlation with causation**
- **Solution:** Look for multiple data points. Use qualitative data to understand why.

**Pitfall 3: Cherry-picking data**
- **Solution:** Look at all relevant data, not just what supports your opinion.

**Pitfall 4: Ignoring qualitative data**
- **Solution:** Use both quantitative and qualitative data. Numbers tell you what, stories tell you why.

## Tools to Help

Decision Tracker helps you use data effectively:

1. **Track decisions** - Document data used and expected outcomes
2. **Link data sources** - Connect analytics, surveys, and reports
3. **Set review dates** - Remind yourself to check outcomes
4. **Document learnings** - Build organizational knowledge

## Learn More

Want to dive deeper into data-driven decisions? Check out these resources:

**📺 YouTube Videos:**

Learn how to make data-driven decisions:

https://www.youtube.com/watch?v=BI_PFLLYg_w

Avoid analysis paralysis with these techniques:

https://www.youtube.com/watch?v=06zWixJRWJY

**📚 Further Reading:**
- "How to Measure Anything" by Douglas Hubbard - Measuring what matters
- "Thinking, Fast and Slow" by Daniel Kahneman - Understanding cognitive biases

## Conclusion

Data should inform decisions, not paralyze them. The Data-Driven Decision Framework helps teams gather the right data, analyze it properly, and make decisions based on evidence rather than opinions.

**Ready to make data-driven decisions?**

[Try Decision Tracker Free](https://decisiontrackerbot.com/trial) - Track decisions with full context during a 7-day free trial.

---

*Have questions about data-driven decisions? Found strategies that work for your team? I'd love to hear from you at [decisiontrackerforslack@gmail.com](mailto:decisiontrackerforslack@gmail.com).*`
  };

  return frameworks[slug] || null;
};

export const getAllFrameworks = () => {
  const frameworkSlugs = [
    'rapid-decision-framework',
    'consensus-building-framework',
    'data-driven-decision-framework'
  ];
  return frameworkSlugs.map(slug => ({
    slug,
    ...getFrameworkMetadata(slug)
  }));
};

const getFrameworkMetadata = (slug) => {
  const metadata = {
    'rapid-decision-framework': {
      title: 'Rapid Decision Framework',
      excerpt: 'Make faster, better decisions with this 4-step framework designed to help teams move from decision paralysis to decisive action.',
      readTime: '10 min read'
    },
    'consensus-building-framework': {
      title: 'Consensus Building Framework',
      excerpt: 'Get everyone on the same page with this 5-stage framework for building consensus and getting buy-in from your entire team.',
      readTime: '12 min read'
    },
    'data-driven-decision-framework': {
      title: 'Data-Driven Decision Framework',
      excerpt: 'Let the numbers guide you with this 6-step framework for making evidence-based decisions without analysis paralysis.',
      readTime: '15 min read'
    }
  };
  return metadata[slug] || {};
};



// WEBPACK FOOTER //
// ./src/data/frameworkContent.js